import type { ComponentProps, ReactNode } from 'react';
import type { CadenceStepTypePure } from '../CGStep/types';
import { cn } from '../cn';
import CTSContentEditingPure, {
  type SiftConfig,
  type StepData,
  type StepType,
} from '../CGStep/CTSContentEditingPure';
import type { ReminderAction } from '../ManualSelect/ManualSelectPure';
import type { TipTapDocument, TipTapVariable } from '../Input2/TipTapInput';
import { CampaignDayHeaderPure } from './CampaignDayHeaderPure';
import { CampaignStepRowPure } from './CampaignStepRowPure';
import { CampaignDayAddButtonPure } from './CampaignDayAddButtonPure';
import { CampaignStepPickerPure } from './CampaignStepPickerPure';
import { canAddAnyStepToBlock } from './blockStepRules';
import type { CampaignDayCardStep } from './stepDisplayInfo';

/**
 * Step types that use the inline editor on click. Declared as a `const`
 * tuple so we can derive the union (`InlineEditableType`) from it — this
 * lets us narrow `step.type: CadenceStepTypePure` to a member of the editor's
 * own `StepType` union via the typeguard below, instead of using a loose
 * `as StepType` cast.
 *
 * Every entry MUST also be a member of `StepType` from CTSContentEditingPure
 * (i.e. anything except `delay` / `wait_for_connect`). TypeScript enforces
 * this implicitly via the StepType-typed prop on `<CTSContentEditingPure>`.
 */
const INLINE_EDITABLE_TYPES_LIST = [
  'visit',
  'connect',
  'message',
  'inmail',
  'email',
  'likeRecentPosts',
  'follow',
  'reminder',
  'sift',
  'voice_note',
  'call',
] as const satisfies ReadonlyArray<StepType>;

type InlineEditableType = (typeof INLINE_EDITABLE_TYPES_LIST)[number];

const INLINE_EDITABLE_TYPES: ReadonlySet<string> = new Set(INLINE_EDITABLE_TYPES_LIST);

function isInlineEditable(t: CadenceStepTypePure): t is InlineEditableType {
  return INLINE_EDITABLE_TYPES.has(t);
}

export type { CampaignDayCardStep } from './stepDisplayInfo';

/**
 * `Omit<ComponentProps<'div'>, 'children'>` brings in `className`, `style`,
 * `onClick`, `data-*`, etc. for free — callers don't need to add ad-hoc
 * passthrough props. We drop `children` because the card's body is fully
 * driven by structured props (`steps`, picker / editor flags), not
 * arbitrary children.
 */
export type CampaignDayCardPureProps = Omit<ComponentProps<'div'>, 'children'> & {
  date: Date;
  steps: CampaignDayCardStep[];
  /**
   * When true, the entire card (border, background, dividers) renders in
   * purple to signal that the whole day block diverges from its template
   * (e.g. the day was added/edited by the user, not inherited from template).
   * Per-row purple tint via `step.isDifferentFromTemplate` is independent.
   */
  isDifferentFromTemplate: boolean | undefined;
  /**
   * When true the footer renders the step-type picker in place of the
   * "+ add step" button. Parent owns this state so the picker can be opened
   * and closed via clicks on the add button / cancel button.
   */
  isPickingStepType: boolean | undefined;
  /**
   * When set, the row with this id is replaced with the inline editor.
   * Only applies to step types in INLINE_EDITABLE_TYPES — clicking other
   * types still fires onEditStep but the host decides what to do.
   */
  editingStepId: string | undefined;
  /**
   * When set, the footer renders the inline editor for adding a new step of
   * this type (instead of the picker or the add-step button). Used for
   * content step types (connect, message, inmail, email) — action-only types
   * (follow, likeRecentPosts) are created immediately by the host without
   * passing through this prop.
   */
  addingStepType: CadenceStepTypePure | undefined;
  /**
   * Per-type disabled reasons forwarded to the picker. Lets the host disable
   * step types based on context the picker doesn't otherwise know about
   * (e.g. `{ connect: 'You are already connected to this contact.' }` when
   * the LinkedIn account is already linked).
   */
  disabledStepReasons: Partial<Record<CadenceStepTypePure, string>> | undefined;
  /** Variables for @ mentions inside the inline editor. */
  variables: TipTapVariable[] | undefined;
  /** Documents for / mentions inside the inline editor. */
  documents: TipTapDocument[] | undefined;

  // Method props are required (no `?`, no `| undefined`). Hosts that
  // genuinely don't care about a callback should pass an explicit no-op
  // arrow function so the contract is visible at the call site.
  onEditStep: (stepId: string) => void;
  onDeleteStep: (stepId: string) => void;
  /**
   * Fired when the user clicks the "Mark done" button on an
   * action-required reminder row. Hosts wire this to the existing
   * `markFinished` mutation so the reminder transitions to the
   * `'finished'` state and disappears from the active list.
   */
  onMarkStepFinished: (stepId: string) => void;
  onAddStep: () => void;
  /** Fired when the user picks a step type from the picker. */
  onPickStepType: (type: CadenceStepTypePure) => void;
  /** Fired when the user dismisses the picker without choosing a type. */
  onCancelPickStepType: () => void;
  /** Fired when the inline EDIT editor's Save button is clicked. */
  onSaveStep: (stepId: string, data: StepData) => void | Promise<void>;
  /** Fired when the inline edit editor's Cancel button is clicked. */
  onCancelEditStep: () => void;
  /** Fired when the inline ADD editor's Add button is clicked. */
  onSaveNewStep: (type: CadenceStepTypePure, data: StepData) => void | Promise<void>;
  /** Fired when the inline add editor's Cancel button is clicked. */
  onCancelAddStep: () => void;
  /** Render prop for the sift condition builder (used by sift step editor). */
  renderSiftConditionBuilder: (props: {
    value: SiftConfig;
    onChange: (value: SiftConfig) => void;
  }) => ReactNode;
  /** Reminder editor — callback to create a new reminder action by name. */
  onCreateReminder: (name: string) => Promise<void>;
  /** Reminder editor — fires when the reminder editor reports an error. */
  onReminderError: (message: string) => void;

  /** Reminder editor — list of available reminder actions. */
  reminderActions: ReminderAction[] | undefined;
  /** Reminder editor — true while the actions list is loading. */
  reminderActionsLoading: boolean | undefined;
  /** Reminder editor — true while a new reminder action is being created. */
  reminderCreating: boolean | undefined;
  /** Reference time for the header's "today" detection. Defaults to `new Date()`. */
  now: Date | undefined;
  /**
   * Forwarded to `CampaignDayHeaderPure` — when set, the "Not in
   * template" / "See in template" tag in the header becomes a real link
   * to this URL (typically `/templates/{templateId}`). Undefined hides
   * the link affordance and falls back to a read-only badge / nothing.
   */
  templateHref: string | undefined;
  /**
   * Forwarded to `CampaignStepPickerPure`. Use `'sm'` in narrow contexts
   * (e.g. the wait-for-connect No-branch pink well) so the option
   * buttons fit without wrapping.
   */
  pickerOptionSize?: 'sm' | 'md';
  /** When true, voice_note step type is available in the picker. */
  voiceEnabled?: boolean;
  /** When true, the call step type is available (team voice-calling flag). */
  voiceCallingEnabled?: boolean;
  /**
   * Injected dialer for an action-required `call` step. Forwarded to the row so the rep
   * can place the call in place. Omitted by read-only hosts → the button doesn't render.
   */
  renderCadenceCallButton?: (args: { cadenceStepId: string }) => ReactNode;
  /** Completes a `call` step handled outside the dialer — see `CallStepActionsPure`. */
  onMarkCallStepDone?: (args: { cadenceStepId: string; outcome: string; notes?: string; callbackAt?: number }) => Promise<void> | void;
  /**
   * Display-only mode for hosts that can show the step plan but can't
   * edit it (e.g. the extension sidepanel). Hides the add-step footer
   * and renders rows without click-to-edit / mark-done affordances.
   * The method props stay required — readOnly hosts pass no-ops.
   */
  readOnly?: boolean;
};

export function CampaignDayCardPure({
  date,
  steps,
  now,
  isDifferentFromTemplate,
  isPickingStepType,
  editingStepId,
  addingStepType,
  disabledStepReasons,
  variables,
  documents,
  onEditStep,
  onDeleteStep,
  onMarkStepFinished,
  onAddStep,
  onPickStepType,
  onCancelPickStepType,
  onSaveStep,
  onCancelEditStep,
  onSaveNewStep,
  onCancelAddStep,
  renderSiftConditionBuilder,
  reminderActions,
  reminderActionsLoading,
  onCreateReminder,
  reminderCreating,
  onReminderError,
  templateHref,
  pickerOptionSize,
  voiceEnabled,
  voiceCallingEnabled,
  renderCadenceCallButton,
  onMarkCallStepDone,
  readOnly,
  className,
  ...divProps
}: CampaignDayCardPureProps) {
  // "Today" detection drives a subtle blue tint on the card so the
  // urgent day pops in a long timeline. `toDateString()` strips the time
  // component, so two dates on the same calendar day produce identical
  // strings — cheaper than the local-midnight Date construction we used
  // before. Template-diff (purple) tint takes precedence — diverged days
  // are the more important signal.
  const nowDate = now ?? new Date();
  const isToday = date.toDateString() === nowDate.toDateString();
  const todayTint = isToday && !isDifferentFromTemplate;

  return (
    <div
      {...divProps}
      data-testid="campaign-day-card"
      data-different-from-template={isDifferentFromTemplate ? 'true' : 'false'}
      data-today={isToday ? 'true' : 'false'}
      className={cn(
        'flex w-full flex-col overflow-hidden rounded-lg border shadow-xs',
        isDifferentFromTemplate
          ? 'border-purple-300 bg-purple-50/40 dark:border-purple-700 dark:bg-purple-950/20'
          : todayTint
            ? 'border-mantis-200 bg-mantis-50/60 dark:border-mantis-800 dark:bg-mantis-950/30'
            : 'border-lynch-200 bg-white dark:border-slate-800 dark:bg-slate-900',
        className,
      )}>
      <div
        className={cn(
          'border-b px-3 py-2',
          isDifferentFromTemplate
            ? 'border-purple-200 dark:border-purple-800'
            : todayTint
              ? 'border-mantis-200 dark:border-mantis-800'
              : 'border-lynch-200 dark:border-slate-800',
        )}
        data-testid="campaign-day-header-wrapper">
        <CampaignDayHeaderPure
          date={date}
          now={now}
          isDifferentFromTemplate={isDifferentFromTemplate}
          templateHref={templateHref}
        />
      </div>
      <div
        className={cn(
          'flex flex-col divide-y',
          isDifferentFromTemplate
            ? 'divide-purple-200 dark:divide-purple-800'
            : 'divide-lynch-200 dark:divide-slate-800',
        )}
        data-testid="campaign-day-steps">
        {steps.length === 0 && (
          <div
            className="px-3 py-2 text-xs italic text-lynch-500 dark:text-lynch-400"
            data-testid="campaign-day-empty">
            No steps on this day
          </div>
        )}
        {steps.map((step) => {
          // Calling the typeguard inline (instead of stashing its result in
          // a boolean) lets TypeScript narrow `step.type` from
          // CadenceStepTypePure → InlineEditableType inside the branch — no
          // `as StepType` cast required at the editor mount below.
          if (editingStepId === step.id && isInlineEditable(step.type)) {
            return (
              <div
                key={step.id}
                className="px-3 py-2"
                data-testid="campaign-step-editor"
                data-editing-step-id={step.id}
                data-editing-step-type={step.type}>
                <CTSContentEditingPure
                  stepType={step.type}
                  stepData={{
                    subject: step.subject,
                    body: step.body2,
                    postsToLike: step.postsToLike,
                    cc: step.cc,
                    bcc: step.bcc,
                    // SiftConfigPure (graph type) and SiftConfig (editor type)
                    // are structurally identical — single cast at the bridge.
                    siftConditions: step.siftConditions as SiftConfig | null | undefined,
                    onlyOpenLink: step.onlyOpenLink,
                  }}
                  canPersonalize={false}
                  variables={variables}
                  documents={documents}
                  renderSiftConditionBuilder={renderSiftConditionBuilder}
                  reminderActions={reminderActions}
                  reminderActionsLoading={reminderActionsLoading}
                  onCreateReminder={onCreateReminder}
                  reminderCreating={reminderCreating}
                  onReminderError={onReminderError}
                  onSubmit={(data) => onSaveStep?.(step.id, data)}
                  onCancel={() => onCancelEditStep?.()}
                  onDelete={
                    onDeleteStep != null ? () => onDeleteStep(step.id) : undefined
                  }
                />
              </div>
            );
          }
          return (
            <CampaignStepRowPure
              key={step.id}
              step={step}
              onEdit={readOnly ? undefined : onEditStep}
              onMarkFinished={readOnly ? undefined : onMarkStepFinished}
              renderCadenceCallButton={readOnly ? undefined : renderCadenceCallButton}
              onMarkCallStepDone={readOnly ? undefined : onMarkCallStepDone}
            />
          );
        })}
      </div>
      {addingStepType != null && isInlineEditable(addingStepType) && (
        <div
          className="border-t border-lynch-200 px-3 py-2 dark:border-slate-800"
          data-testid="campaign-step-add-editor"
          data-adding-step-type={addingStepType}>
          <CTSContentEditingPure
            stepType={addingStepType}
            stepData={{}}
            canPersonalize={false}
            variables={variables}
            documents={documents}
            renderSiftConditionBuilder={renderSiftConditionBuilder}
            reminderActions={reminderActions}
            reminderActionsLoading={reminderActionsLoading}
            onCreateReminder={onCreateReminder}
            reminderCreating={reminderCreating}
            onReminderError={onReminderError}
            submitLabel="Add"
            onSubmit={(data) => onSaveNewStep?.(addingStepType, data)}
            onCancel={() => onCancelAddStep?.()}
          />
        </div>
      )}
      {addingStepType == null && isPickingStepType && (
        <CampaignStepPickerPure
          existingTypes={steps.map((s) => s.type)}
          disabledTypeReasons={disabledStepReasons}
          optionSize={pickerOptionSize}
          isDifferentFromTemplate={isDifferentFromTemplate}
          voiceEnabled={voiceEnabled}
          voiceCallingEnabled={voiceCallingEnabled}
          onPickType={(type) => onPickStepType?.(type)}
          onCancel={onCancelPickStepType}
          // Top border + faint container bg separate the picker from the
          // step rows above. Tint matches the host card so the picker
          // reads as part of the same surface.
          className={cn(
            'border-t',
            isDifferentFromTemplate
              ? 'border-purple-200 bg-purple-50/50 dark:border-purple-800 dark:bg-purple-950/30'
              : 'border-lynch-200 bg-lynch-50/50 dark:border-slate-800 dark:bg-slate-950/50',
          )}
        />
      )}
      {!readOnly && addingStepType == null && !isPickingStepType && (
        <CampaignDayAddButtonPure
          onClick={onAddStep}
          disabled={!canAddAnyStepToBlock(steps.map((s) => s.type))}
          disabledTooltip="No more step types can be added to this day"
          isDifferentFromTemplate={isDifferentFromTemplate}
        />
      )}
    </div>
  );
}
